import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/_atoms/command";
import { UserAvatar } from "@/domain/user/components/user-avatar";
import { useSmartUserList } from "@/domain/user/hooks/api/use-smart-user-list";
import type { Editor } from "@tiptap/react";

interface MentionSuggestionProps {
  editor: Editor | null;
  query: string;
  position: { top: number; left: number } | null;
  onSelect: (userId: string, label: string) => void;
  onClose: () => void;
}

/**
 * Mention suggestion dropdown for Tiptap editor.
 *
 * **Security**: Uses `useSmartUserList` to fetch user data safely in both
 * public and private contexts. In public dashboards (read-only), this should
 * not be shown as the editor is disabled.
 */
export function MentionSuggestion({ editor, query, position, onSelect, onClose }: MentionSuggestionProps) {
  const { t } = useTranslation("common");
  // SECURITY: Use smart hook that switches between public/private data sources
  const { userList } = useSmartUserList();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const commandRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filteredUsers = useMemo(() => {
    if (!userList) {
      return [];
    }
    return userList
      .filter((user) => {
        // If query is empty, show all users (or first 10)
        if (!query || query.trim() === "") {
          return true;
        }

        const searchQuery = query.toLowerCase().trim();
        const fullname = user.fullname?.toLowerCase() || "";
        const username = user.username?.toLowerCase() || "";
        const email = user.email?.toLowerCase() || "";

        // Check if any field contains the query
        return (
          fullname.includes(searchQuery) ||
          username.includes(searchQuery) ||
          email.includes(searchQuery) ||
          // Also check if fullname starts with the query (for better matching)
          fullname.startsWith(searchQuery) ||
          username.startsWith(searchQuery)
        );
      })
      .slice(0, 10);
  }, [userList, query]);

  useEffect(() => {
    // Always reset to first item when query changes
    setSelectedIndex(0);
    // Reset refs when query changes
    itemRefs.current = [];
  }, [query, filteredUsers.length]);

  useEffect(() => {
    // Scroll selected item into view when selectedIndex changes
    const selectedItem = itemRefs.current[selectedIndex];
    if (selectedItem && listRef.current) {
      const listElement = listRef.current;
      const itemOffsetTop = selectedItem.offsetTop;
      const itemHeight = selectedItem.offsetHeight;
      const listScrollTop = listElement.scrollTop;
      const listHeight = listElement.clientHeight;

      // Check if item is outside visible area
      if (itemOffsetTop + itemHeight > listScrollTop + listHeight) {
        // Item is below visible area, scroll to show it at the bottom
        listElement.scrollTop = itemOffsetTop + itemHeight - listHeight;
      } else if (itemOffsetTop < listScrollTop) {
        // Item is above visible area, scroll to show it at the top
        listElement.scrollTop = itemOffsetTop;
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (!editor || !position) {
      return;
    }

    if (filteredUsers.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!position) return;

      // Only handle keys when the mention dropdown is visible
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) => (prev + 1) % filteredUsers.length);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) => (prev - 1 + filteredUsers.length) % filteredUsers.length);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        // Always select the first item (selectedIndex should be 0, but ensure it's valid)
        const indexToSelect = selectedIndex >= 0 && selectedIndex < filteredUsers.length ? selectedIndex : 0;
        if (filteredUsers[indexToSelect]) {
          const user = filteredUsers[indexToSelect];
          onSelect(user.id, user.fullname || user.username || user.email || "");
        }
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
    };

    // Attach to the editor's DOM to intercept before TipTap handles it
    const editorElement = editor.view.dom;
    editorElement.addEventListener("keydown", handleKeyDown, true); // Use capture phase
    
    return () => {
      editorElement.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [editor, position, filteredUsers, selectedIndex, onSelect, onClose]);

  if (!position) {
    return null;
  }

  // Show dropdown even if no users match, as long as we have a position and query
  // This allows the user to see that mentions are working
  if (filteredUsers.length === 0 && query === "") {
    return null;
  }

  const content = (
    <div
      className="pointer-events-auto z-50 w-[300px] rounded-md border border-border bg-white shadow-md"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <Command ref={commandRef} className="overflow-hidden">
        <CommandList ref={listRef} className="overscroll-contain" onWheel={(e) => e.stopPropagation()}>
          <CommandEmpty>{t("search.noResults")}</CommandEmpty>
          <CommandGroup>
            {filteredUsers.map((user, index) => {
              const label = user.fullname || user.username || user.email || "";
              return (
                <CommandItem
                  key={user.id}
                  value={user.id}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  onSelect={() => {
                    onSelect(user.id, label);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelect(user.id, label);
                  }}
                  className={index === selectedIndex ? "bg-accent" : ""}
                >
                  <div className="flex items-center gap-2">
                    <UserAvatar user={user} size="sm" />
                    <span>{label}</span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );

  // Portal to document.body to avoid nested button issues
  return typeof document !== "undefined" ? createPortal(content, document.body) : null;
}

